import { BatchLinkPlugin } from "@-/com.npmjs.orpc__client/plugins";
import { isSlowOrpcProcedure } from "#orpc-procedure-deadlines";

/**
 * Batch response mode (oRPC's `BatchResponseMode`). Inlined as a string-literal
 * union because oRPC does not re-export the type name from `@orpc/client/plugins`.
 */
type BatchResponseMode = "buffered" | "streaming";

export interface CreateOrpcLinkPluginsOptions {
  /**
   * Batch response mode. Defaults to `buffered` in Node (Jest, SSR) and `streaming` elsewhere.
   *
   * @see https://orpc.dev/docs/plugins/batch-requests#batch-mode
   */
  mode?: BatchResponseMode;
}

/**
 * Link plugins shared by production and test oRPC clients (batch requests).
 *
 * @see https://orpc.dev/docs/plugins/batch-requests
 */
function resolveDefaultBatchMode(): BatchResponseMode {
  // React Native defines `globalThis.window` (the RN runtime aliases it to the
  // global), so the `window`-exists heuristic below misclassifies it as a browser
  // and picks `"streaming"`. RN's fetch/Blob stack can't decode a streamed batch
  // response — reassembling the streamed body makes it attempt `new Blob([arrayBuffer])`,
  // which Hermes rejects with "Creating blobs from 'ArrayBuffer' and 'ArrayBufferView'
  // are not supported". Force `"buffered"` on RN so batched fan-out calls (e.g. the
  // grapheme-file editor's concurrent card fetches) work.
  const isReactNative = typeof navigator !== "undefined" && navigator.product === "ReactNative";
  return isReactNative || globalThis.window === undefined ? "buffered" : "streaming";
}

export function createOrpcLinkPlugins(options: CreateOrpcLinkPluginsOptions = {}) {
  const mode = options.mode ?? resolveDefaultBatchMode();

  return [
    new BatchLinkPlugin({
      // A batch is one HTTP request under ONE deadline (the default one; see
      // `fetch-orpc-with-deadline.ts`). A procedure with a longer budget travels alone, so it can
      // neither be cut off by the batch deadline nor, in buffered mode, hold its fast siblings for
      // its whole budget.
      exclude: ({ path }) => isSlowOrpcProcedure(path),
      groups: [
        {
          condition: () => true,
          context: {},
        },
      ],
      mode,
    }),
  ];
}
